import { useState, useMemo } from 'react';
import { focusOwnsKey, isTypingTarget } from '../keyboard';
import { hasOpenSurface } from '../useDismiss';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  completeTask,
  listTasks,
  updateTask,
  type Task,
  type TaskFilter,
  type TaskStatus,
} from '../api';
import { goalChipsFor } from '../goalFocus';
import { TaskStatusControl, type ReadbackClaim } from './TaskStatusControl';
import { ReadbackSlot, useCommandReadback } from './CommandReadback';
import { StatusBadge, PriorityMark } from './StatusBadge';
import { BlockedByPill, blockersAreTheContent } from './BlockedByPill';
import { ChecklistLine } from './ChecklistLine';
import { QueryState } from './QueryState';
import { dueLook, dueClass, dueTitle } from '../taskVocab';
import { useAccountDay } from '../accountDay';
import { soleSelectedTask, useCursorFollowsOpenTask, useTaskSelection } from '../useTaskSelection';
import { TaskCommandBar } from './TaskCommands';
import { inlineKeys } from '../keys';
import { useKeys } from '../keyScope';
import { ParentLink } from './ParentLink';
import { SubtaskPill } from './SubtaskPill';
import { AssigneeChip } from './AssigneeChip';
import { useAssignees } from '../useAssignees';
import { WipNotice, boardFilter, onTheBoard } from './WipNotice';

/**
 * The board.
 *
 * Agreed scope since §6.3 — "Todos — filterable list/board across all goals and projects" — and
 * never built. The owner asked for it in their own words: "no easy way of transitioning tasks
 * between states and selecting what I want to work on, maybe similar column view as jira uses".
 *
 * Three columns, not five. `blocked` is derived, not stored, so a blocked column could never be
 * dropped into and nothing could ever leave it; it is a badge. `dropped` is an ending rather
 * than a place work sits, so it lives in the menu the card already carries. `done` is the drop
 * target below, not a fourth column — a column of everything you have ever finished is a
 * scrolling archive, and this screen is for choosing what to do next.
 *
 * Drag is the convenience; the mechanism is the Phase 37 control on every card. That is the
 * house pattern since Phase 17 — select-then-act, not touch-drag — so the board works on a phone
 * and from a keyboard without a second implementation.
 */

const COLUMNS: { status: TaskStatus; title: string; hint: string }[] = [
  { status: 'inbox', title: 'Inbox', hint: 'Captured, not yet decided' },
  /* "not yet scheduled", not "waiting for a slot": a card on this board can also carry the
     blocked badge, and one screen over the Waiting rail uses that word for a promise somebody
     outside the account is holding. Neither is what Next means. */
  { status: 'next', title: 'Next', hint: 'Decided, not yet scheduled' },
  { status: 'in_progress', title: 'In progress', hint: 'Started' },
];

function BoardCard({
  task,
  goalChips,
  colorOf,
  labelNames,
  onOpen,
  onDragStart,
  onFocusCard,
  onMenu,
  isCursor,
  isSelected,
  nested = false,
  myUserId,
  nameFor,
  canClaim,
  blockersAreContent = false,
}: {
  task: Task;
  /** Whether this board is filtered to blocked work — see `blockersAreTheContent`. */
  blockersAreContent?: boolean;
  /** Drawn directly under its own parent, so the parent chip would repeat the card above it. */
  nested?: boolean;
  /** Who the viewer is, so their own name reads as "you". */
  myUserId: string | null;
  nameFor: (userId: string | null) => string | null;
  canClaim: (projectId: string | null) => boolean;
  goalChips: ReadonlyMap<string, { title: string; colorClass: string }>;
  /** The same chain the list rows use — see `resolveTaskColor`. */
  colorOf: (task: Task) => { colorClass: string | null; from: string };
  labelNames: ReadonlyMap<string, string>;
  onOpen: (id: string) => void;
  onDragStart: () => void;
  onFocusCard: () => void;
  onMenu: (event: React.MouseEvent, task: Task) => void;
  /** The keyboard cursor, and whether this card is in the selection it acts on. */
  isCursor?: boolean;
  isSelected?: boolean;
}) {
  /* The same calendar the list row judges by — the account's, not the browser's. */
  const { timezone, today } = useAccountDay();
  const allChips = goalChipsFor(task, goalChips);
  const chip = allChips[0];
  // Same rule as the list row: the first goal, then a count of the rest, so the two views of one
  // task do not disagree about how many goals it serves.
  const moreChips = allChips.slice(1);
  return (
    <li
      /*
        The card's colour is the *task's* colour, not its goal's.

        This read `chip?.colorClass` — the goal chip — so a task carrying its own colour, or taking
        one from a label, showed none of it on the board, and a task with no goal had no colour at
        all however it was set. The list rows next door run the full chain. Two views of one task
        disagreeing about what colour it is was the whole thing `resolveTaskColor` exists to stop.
      */
      className={[
        'board-card',
        task.isBlocked ? 'is-blocked' : '',
        isCursor ? 'is-cursor' : '',
        isSelected ? 'is-selected' : '',
        colorOf(task).colorClass ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-cursor={isCursor || undefined}
      data-nested={nested || undefined}
      /* Where the task panel hands focus back when it closes — see `taskOpener`. */
      data-task-id={task.id}
      title={colorOf(task).colorClass ? `Colour from ${colorOf(task).from}` : undefined}
      data-testid="board-card"
      onContextMenu={(e) => onMenu(e, task)}
      /*
        Touching a card puts the cursor on it, exactly as touching a row does on the list and a
        row does on the goal tree. Nothing is prevented, so the title button, the parent arrow and
        the drag all still do their own job — this only settles which card `1`/`2`/`3` mean.
      */
      onMouseDown={onFocusCard}
      draggable
      onDragStart={(e) => {
        // The id travels in `dataTransfer`, not in React state: state set at dragstart is only
        // visible to the drop handler if a re-render happened in between, which is not
        // guaranteed. I-25, and the same shape the timeline has used since Phase 8.
        e.dataTransfer.setData('planner/task', task.id);
        e.dataTransfer.effectAllowed = 'move';
        onDragStart();
      }}
    >
      <button className="link board-card-title" onClick={() => onOpen(task.id)}>
        {task.title}
      </button>
      {/* On its own line — a card has the vertical room a row does not, so the name never has to
          compete with the meta strip for the space that runs out first. */}
      {/* Suppressed when the card is drawn directly under its parent — the chip is for surfaces
          where the parent is nowhere in sight, which is exactly what adjacency removes. The list
          has made the same call since Phase 25. */}
      {task.parent && !nested && <ParentLink parent={task.parent} onOpen={onOpen} />}
      <span className="task-meta">
        <AssigneeChip
          task={task}
          myUserId={myUserId}
          name={nameFor(task.assigneeUserId)}
          actionable={canClaim(task.projectId)}
        />
        <SubtaskPill task={task} />
        {/* Same line the list row carries, for the reason the pill above is shared. */}
        <ChecklistLine task={task} />
        {/*
          No checkbox, no start button, no move menu.

          The card used to carry all three, and the menu's options were the names of the columns
          either side of it — the same information three times, which is why a one-line task needed
          97 pixels of card. On a board the column *is* the state. Moving between them is drag, or
          the cursor keys, which is also the first honest touch and keyboard story this had.
        */}
        {/*
          The badge *and* the name. The badge is the state and the column is not, so it stays; what
          was missing is who to chase. See `BlockedByPill` — this card had none of this text, so
          the board carried strictly less than the list it shares a filter with.
        */}
        {task.isBlocked && <StatusBadge status="blocked" compact />}
        <BlockedByPill task={task} full={blockersAreContent} />
        <PriorityMark priority={task.priority} />
        {task.estimateMinutes && <span className="pill">{task.estimateMinutes}m</span>}
        {/*
          The board drew blocked, priority, estimate, goal and labels — and never the date. So the
          two views of one filter carried different information, and the one that looks built for
          triage carried less.
        */}
        {(() => {
          const look = dueLook(task, today, timezone);
          return look ? (
            <span className={dueClass(look)} title={dueTitle(look)}>
              {look.text}
            </span>
          ) : null;
        })()}
        {moreChips.length > 0 && (
          <span
            className="pill goal-chip"
            title={`Also: ${moreChips.map((c) => c.title).join(', ')}`}
          >
            +{moreChips.length}
          </span>
        )}
        {chip && (
          <span className={`pill goal-chip ${chip.colorClass}`} title={`Goal: ${chip.title}`}>
            {chip.title}
          </span>
        )}
        {/* Labels, the same two-then-a-count the list rows show. The board was the last task
            surface not naming them. */}
        {task.labelIds
          .map((id) => labelNames.get(id))
          .filter((name): name is string => name !== undefined)
          .slice(0, 2)
          .map((name) => (
            <span key={name} className="pill label-chip" title={`Label: ${name}`}>
              {name}
            </span>
          ))}
        {task.labelIds.length > 2 && (
          <span className="pill label-chip" title="Open the task to see them all">
            +{task.labelIds.length - 2}
          </span>
        )}
      </span>
    </li>
  );
}

export function TaskBoard({
  filter,
  showSubtasks,
  goalChips,
  colorOf,
  labelNames,
  onOpen,
  onMenu,
  openTaskId,
}: {
  /** Shared with the list, so the overview tiles and the filter row drive the board for free. */
  filter: TaskFilter;
  /** The list's own toggle, governing both views — see the comment beside it in `TaskList`. */
  showSubtasks: boolean;
  goalChips: ReadonlyMap<string, { title: string; colorClass: string }>;
  colorOf: (task: Task) => { colorClass: string | null; from: string };
  labelNames: ReadonlyMap<string, string>;
  onOpen: (id: string) => void;
  onMenu: (event: React.MouseEvent, task: Task) => void;
  /** Which task the app has open, so this cursor follows it too — see `useCursorFollowsOpenTask`. */
  openTaskId?: string | null;
}) {
  const queryClient = useQueryClient();
  const [over, setOver] = useState<string | null>(null);

  // Its three columns in one query — see `boardFilter`, which Today's sentence reads too.
  const columnsFilter = boardFilter(filter);
  const tasks = useQuery({
    queryKey: ['tasks', columnsFilter],
    queryFn: () => listTasks(columnsFilter),
  });
  const { myUserId, nameFor, canClaim } = useAssignees(tasks.data ?? []);

  /*
    The done strip doubles as undo.

    Dropping onto "Done" is irreversible-looking, and the nearest undo — find the task again with
    "show done" ticked — is exactly the round trip this screen exists to remove. Today's finished
    work sits under the columns with its checkbox still live.
  */
  const doneToday = useQuery({
    queryKey: ['tasks', { ...filter, status: 'done', completedOn: 'today' }],
    queryFn: () => listTasks({ ...filter, status: 'done', includeDone: true }),
  });

  /*
    The same keyboard the list has.

    The board is the better view for triage and could not be driven at all: the list has a cursor,
    a selection and a command set, and this had none of it. The cursor is two-dimensional here, so
    `useTaskSelection` drives it over a flat list built column by column, and the handlers below
    translate between that flat order and the grid the reader sees.
  */
  /*
    Memoised, like the list's own `ids`.

    `useTaskSelection` reconciles its cursor and selection in an effect keyed on the id array, so
    a fresh array on every render runs that reconciliation on every render — which is both wasteful
    and exactly the kind of churn that loses a selection between a keypress and the paint after it.
  */
  /* Which rows the board draws — the rule, and why, is `onTheBoard`'s. */
  const visible = useMemo(
    () => onTheBoard(tasks.data ?? [], showSubtasks),
    [tasks.data, showSubtasks],
  );

  /*
    A parent's children sit with it, as they do on the list.

    "Open every parent" turned subtasks into sibling cards and let the column's global sort scatter
    them: the three children of `Bring up SA (Aug 21)` landed at positions 3, 9 and 13, linked only
    by a `↱ Parent` chip that could not tell two identically-titled `Bring up SA` parents apart. So
    the board had a different structural model from the list for the same data and the same toggle.

    Each column is re-laid rather than re-sorted: the query order still decides which parents come
    first, and a parent simply carries its own children with it, in written order — the same rule
    the list settled on. A child whose parent is in a *different* column keeps its own place and
    its chip, because there is nothing here to sit under.
  */
  const byColumn = useMemo(
    () =>
      COLUMNS.map((c) => {
        const here = visible.filter((t) => t.status === c.status);
        const present = new Set(here.map((t) => t.id));
        const children = new Map<string, Task[]>();
        for (const t of here) {
          if (t.parentTaskId && present.has(t.parentTaskId)) {
            children.set(t.parentTaskId, [...(children.get(t.parentTaskId) ?? []), t]);
          }
        }
        for (const list of children.values()) {
          list.sort((a, b) => a.sortOrder - b.sortOrder || a.createdAt.localeCompare(b.createdAt));
        }
        const out: Task[] = [];
        for (const t of here) {
          if (t.parentTaskId && present.has(t.parentTaskId)) continue;
          out.push(t);
          out.push(...(children.get(t.id) ?? []));
        }
        return out;
      }),
    [visible],
  );

  /** Ids drawn immediately under their own parent, so the card can drop the chip that says so. */
  const groupedUnderParent = useMemo(() => {
    const ids = new Set<string>();
    for (const column of byColumn) {
      const present = new Set(column.map((t) => t.id));
      for (const t of column) {
        if (t.parentTaskId && present.has(t.parentTaskId)) ids.add(t.id);
      }
    }
    return ids;
  }, [byColumn]);

  /*
    What each column *holds*, as opposed to how many cards it is currently drawing.

    These are not the same number and the heading means the first one. `visible` drops a subtask
    whose parent is already on the board, so ticking "Open every parent" — a display toggle that
    changes nothing about any task — used to move `Inbox 12` to `Inbox 14` while the status chips
    above it, which are right, never budged. A count wearing the word "Inbox" is a claim about
    the inbox, not about the rendering.

    That was the right half of the answer and only half of it. The heading counts the column and the
    reader counts the cards, and when the two differ nobody is wrong: "Inbox 14, I count 12" is a
    correct observation about a correct number. So the badge keeps the column's contents and the
    heading *says* when fewer are drawn — `Inbox 14 · 12 shown`. A badge counts the noun it names,
    and admits it when the rendering beside it shows fewer.

    Only when they differ. A qualifier on every column to explain the columns that need one is the
    same noise the count itself was accused of.
  */
  const heldByColumn = useMemo(() => {
    const all = tasks.data ?? [];
    return COLUMNS.map((c) => all.filter((t) => t.status === c.status).length);
  }, [tasks.data]);
  const flatIds = useMemo(() => byColumn.flatMap((list) => list.map((t) => t.id)), [byColumn]);
  /* The list's rule, on the list's shared filter: narrowing the columns must not throw away a
     selection assembled across them. See `useTaskSelection`. */
  const selection = useTaskSelection(flatIds, JSON.stringify(columnsFilter));
  const hiddenCount = useMemo(() => {
    // Nothing fetched yet says nothing about what is hidden: reporting the whole selection as
    // hidden for the frame the new query is in flight is a flicker that reads as data loss.
    if (tasks.data === undefined) return 0;
    const shown = new Set(tasks.data.map((t) => t.id));
    return [...selection.selectedIds].filter((id) => !shown.has(id)).length;
  }, [tasks.data, selection.selectedIds]);
  /*
    The board binds `1`, `2` and `3` to a status change, so it carries the same hazard the list
    did: a card opened by the mouse while the cursor sat on another card meant three keys aimed at
    work nobody was looking at. Same rule, same hook, one notion of "current" across both views.
  */
  useCursorFollowsOpenTask(selection, flatIds, openTaskId);

  /** Where the cursor sits in the grid, or null when it is nowhere. */
  const cursorAt = (() => {
    if (selection.cursorId === null) return null;
    for (const [col, list] of byColumn.entries()) {
      const row = list.findIndex((t) => t.id === selection.cursorId);
      if (row !== -1) return { col, row };
    }
    return null;
  })();

  /*
    What the last move did, named, with the way back — the list's readback, on the board.

    The board had none (`01a03813-a118`): `x` on a card, a `1`/`2`/`3`, a drop onto a column or the
    Done strip moved the work and said nothing, so a card sent to Done by a slipped key was gone
    from the columns with only the strip's checkbox to find. Same hook as Tasks and Today, so the
    same act reads the same on all three.
  */
  const readback = useCommandReadback();
  const tasksById = (ids: readonly string[]) =>
    (tasks.data ?? []).filter((t) => ids.includes(t.id));

  const move = useMutation({
    mutationFn: ({ id, status }: { id: string; status: TaskStatus; claim?: ReadbackClaim }) =>
      // Done keeps its own route: it spawns the successor of a recurring task, which a plain
      // status write did not do until Phase 36 and which this board must not regress.
      status === 'done' ? completeTask(id).then((r) => r.task) : updateTask(id, { status }),
    onSuccess: () => queryClient.invalidateQueries(),
    /* A refused move takes down its own card from the line its batch put up, and no later line —
       the batch is one request per card, so its other cards may well have saved. */
    onError: (_error, { id, claim }) => readback.withdraw(claim, [id]),
  });
  /** Move these cards, after saying so — one line for the batch, recorded before the requests. */
  const moveAll = (ids: readonly string[], status: TaskStatus) => {
    const claim = readback.record(status, tasksById(ids));
    for (const id of ids) move.mutate({ id, status, claim });
  };

  /*
    h j k l, and 1 2 3.

    `j`/`k` step within a column and `h`/`l` change column keeping the row, which is what makes a
    grid navigable rather than a list pretending to be one. `1`/`2`/`3` move the *work* to a
    column, matching the list's own bindings, so the same three keys mean the same three states on
    both screens.
  */
  useKeys('board', (e: KeyboardEvent) => {
    if (isTypingTarget()) return;
    // The list's rule: a focused control outside the cards owns Enter and Space.
    if (focusOwnsKey(e)) return;
    if (e.metaKey || e.ctrlKey || e.altKey) return;

    const at = cursorAt;
    const step = (col: number, row: number) => {
      const list = byColumn[col];
      if (!list || list.length === 0) return;
      const target = list[Math.max(0, Math.min(row, list.length - 1))];
      if (target) selection.moveTo(target.id);
    };

    /*
      Extending a run, before the plain moves below.

      Ordered first for the reason the list orders them first: `Shift-ArrowDown` matches
      `e.key === 'ArrowDown'` too, so a later check would move the cursor and never extend. Both
      cases of the letter are matched because the shifted key *is* `'J'`.

      `extendTo` with the neighbouring card rather than `extendBy(±1)`, which is what the list
      uses. `extendBy` walks `flatIds` — every column flattened in order — so from the last card
      of Inbox it would reach into Next, and "extend the selection down a card" has to mean down
      this column. Same machinery, told which card; it grows and never shrinks, exactly as
      shift-click does, because those are one gesture reached by different devices.
    */
    if (e.shiftKey && (e.key === 'J' || e.key === 'j' || e.key === 'ArrowDown')) {
      if (!at) return;
      e.preventDefault();
      const target = byColumn[at.col]?.[at.row + 1];
      if (target) selection.extendTo(target.id);
      return;
    }
    if (e.shiftKey && (e.key === 'K' || e.key === 'k' || e.key === 'ArrowUp')) {
      if (!at || at.row === 0) return;
      e.preventDefault();
      const target = byColumn[at.col]?.[at.row - 1];
      if (target) selection.extendTo(target.id);
      return;
    }
    /*
      Every card on the board, which the list has had since b322421 and this did not.

      Measured in the persona round: pressing `a` on the board left the selection at 1, while on
      the list it took 29 rows. `useTaskSelection` already exposed `selectAll` — the board was
      missing the binding, not the machinery.
    */
    if (e.key === 'a') {
      e.preventDefault();
      return selection.selectAll();
    }
    if (e.key === 'j' || e.key === 'ArrowDown') {
      e.preventDefault();
      return at ? step(at.col, at.row + 1) : step(0, 0);
    }
    if (e.key === 'k' || e.key === 'ArrowUp') {
      e.preventDefault();
      return at ? step(at.col, at.row - 1) : step(0, 0);
    }
    if (e.key === 'h' || e.key === 'ArrowLeft') {
      e.preventDefault();
      return at ? step(Math.max(0, at.col - 1), at.row) : step(0, 0);
    }
    if (e.key === 'l' || e.key === 'ArrowRight') {
      e.preventDefault();
      return at ? step(Math.min(COLUMNS.length - 1, at.col + 1), at.row) : step(0, 0);
    }
    if (e.key === ' ') {
      if (selection.cursorId === null) return;
      e.preventDefault();
      return selection.toggle(selection.cursorId);
    }
    if (e.key === 'Enter') {
      if (selection.cursorId === null) return;
      e.preventDefault();
      return onOpen(selection.cursorId);
    }
    if (e.key === 'Escape') {
      // The same layering the list uses, for the same reason: this handler is on `document` and
      // registers before any popover, so a press that dismissed one must not also wipe the
      // batch behind it. See `hasOpenSurface`.
      if (e.defaultPrevented || hasOpenSurface()) return;
      return selection.clear();
    }

    /*
      Complete, which is the most-used act in the app and was missing from half the Tasks screen.

      `keys.ts` recorded that `x` is not bound here and gave no reason it should not be — and a
      documented divergence is not a justified one. Muscle memory built on the list misfired on the
      board: the press did nothing at all.

      Through `move`, which already routes `done` via `completeTask` and says why in its own
      comment — the recurring successor, the habit entry and the timeline block all hang off
      completion, and a plain status write does none of them. That makes this the same path the
      list's `x` takes, one card at a time, which is also what `useTaskCommand` does for `done`.
    */
    if (e.key === 'x' && selection.targets.length > 0) {
      e.preventDefault();
      const acted = selection.targets;
      moveAll(acted, 'done');
      /*
        And the cursor parks in the gap rather than inheriting the card that takes the place of the
        one that just left — the hazard `holdGap` exists for, carried over rather than rediscovered
        here. On the list one press of `x` completed a task, the cursor slid down, and a second
        press — the natural undo instinct — completed a different one and unblocked a third.
      */
      if (selection.selectedIds.size === 0) selection.holdGap();
      return;
    }

    const column = COLUMNS[Number(e.key) - 1];
    if (column && selection.targets.length > 0) {
      e.preventDefault();
      moveAll(selection.targets, column.status);
    }
  });

  const byStatus = (status: TaskStatus) => visible.filter((t) => t.status === status);
  const inProgress = byStatus('in_progress');

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const finishedToday = (doneToday.data ?? []).filter(
    (t) => t.completedAt !== null && new Date(t.completedAt).getTime() >= startOfToday.getTime(),
  );

  const dropHandlers = (status: TaskStatus) => ({
    onDragOver: (e: React.DragEvent) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setOver(status);
    },
    onDragLeave: (e: React.DragEvent) => {
      // `dragleave` fires when the pointer crosses into a *child* card too, not only when it
      // leaves the column — the same flicker the timeline hit in Phase 8. `relatedTarget` is
      // what is being entered, so if it is still inside, the drag has not left.
      if ((e.currentTarget as HTMLElement).contains(e.relatedTarget as Node | null)) return;
      setOver((c) => (c === status ? null : c));
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      setOver(null);
      const id = e.dataTransfer.getData('planner/task');
      if (id) moveAll([id], status);
    },
  });

  return (
    <div data-testid="board">
      <QueryState query={tasks} />

      <WipNotice count={inProgress.length} />

      {/*
        The same bar the list has, on the same condition.

        The board kept the *selection* model alive — j/k moves a cursor, Space tints a card — and
        then had no command bar at all, so selecting on the board was a dead end that looked like
        it was working. The architect found that; the manager found the other half, that the view
        where every task fits on one screen was the one view with no verbs.
      */}
      {/* The bar and the readback pinned together under the header — see `ReadbackSlot`. */}
      <div className="command-dock">
        {selection.selectedIds.size > 0 && (
          <TaskCommandBar
            targets={selection.targets}
            selectedCount={selection.selectedIds.size}
            hiddenCount={hiddenCount}
            onClearSelection={selection.clear}
            /* The board is handed the scoped filter, so this is the project the columns are showing. */
            projectId={filter.projectId ?? null}
            /* The one selected task, so the bar names it and hides verbs already true of it —
               the same contract the list passes. Not the cursor's: see `soleSelectedTask`. */
            task={soleSelectedTask(selection.selectedIds, tasks.data)}
            /* Recorded before the bar's request, as on the list: the selection is about to clear. */
            onCommitted={(status) => readback.record(status, tasksById(selection.targets))}
            /* A bulk edit gets the sentence and no way back, as on the list: Apply rewrites dates,
               labels, goals and the assignee, and a button would restore status alone. */
            onDescribeEdit={(what, count) =>
              readback.show({
                verb: `Changed ${what} on`,
                landed: null,
                titles:
                  count === 1
                    ? tasksById(selection.targets).map((t) => t.title)
                    : new Array<string>(count).fill(''),
                from: [],
              })
            }
          />
        )}
        <ReadbackSlot readback={readback} />
      </div>

      <div className="board-columns">
        {COLUMNS.map((column, columnIndex) => {
          /*
            The same array the cursor walks, not a second filter over the same data.

            This called `byStatus(column.status)` while `byColumn` — which the keyboard, the flat
            id list and the cursor all use — was built separately. Two derivations of one column is
            how the grouping below could be written, tested at the array level and still not reach
            the screen: the DOM was reading the other one.
          */
          const items = byColumn[columnIndex] ?? [];
          const held = heldByColumn[columnIndex] ?? items.length;
          return (
            <section
              key={column.status}
              className={`board-column ${over === column.status ? 'is-over' : ''}`}
              data-testid={`column-${column.status}`}
              aria-label={column.title}
              {...dropHandlers(column.status)}
            >
              <header className="board-column-head">
                <h3>
                  {column.title} <span className="pill">{held}</span>
                  {/* What the eye can count, when it is fewer — see `heldByColumn`. Subtasks are
                      folded under their parents until "Open every parent" is ticked, and that
                      toggle changes nothing about any task. */}
                  {items.length < held && (
                    <span className="subtext" data-testid={`shown-${column.status}`}>
                      {' '}
                      · {items.length} shown
                    </span>
                  )}
                </h3>
                <span className="subtext">{column.hint}</span>
              </header>
              <ul className="plain board-cards">
                {items.map((task) => (
                  <BoardCard
                    blockersAreContent={blockersAreTheContent(filter)}
                    key={task.id}
                    task={task}
                    nested={groupedUnderParent.has(task.id)}
                    goalChips={goalChips}
                    colorOf={colorOf}
                    labelNames={labelNames}
                    onOpen={onOpen}
                    onMenu={onMenu}
                    myUserId={myUserId}
                    nameFor={nameFor}
                    canClaim={canClaim}
                    onDragStart={() => setOver(null)}
                    onFocusCard={() => selection.moveTo(task.id)}
                    isCursor={selection.cursorId === task.id}
                    isSelected={selection.selectedIds.has(task.id)}
                  />
                ))}
              </ul>
              {items.length === 0 && <p className="muted board-empty">Nothing here.</p>}
            </section>
          );
        })}
      </div>

      {/*
        From `keys.ts`, like the list's.

        This was hand-written and drifted exactly as two documents do: it advertised `1 2 3`, which
        the `?` panel had never heard of, while the panel listed `x`, `Shift-Enter` and `Ctrl-click`
        under a heading that claimed to cover the board and none of which the board binds.
      */}
      {flatIds.length > 0 && (
        <p className="subtext keys-hint">
          {inlineKeys('board').map(([key, what], i) => (
            <span key={key}>
              {i > 0 && ' · '}
              <kbd>{key}</kbd> {what.toLowerCase()}
            </span>
          ))}
        </p>
      )}

      <section
        className={`board-done ${over === 'done' ? 'is-over' : ''}`}
        data-testid="column-done"
        aria-label="Done"
        {...dropHandlers('done')}
      >
        <h3>
          Done today <span className="pill">{finishedToday.length}</span>
        </h3>
        {/*
          The done strip gets the menu too.

          It renders its own row rather than a `BoardCard` — a status box and a title, no colour
          band or chips — so it never picked up the `onContextMenu` the cards have. A finished task
          is where Clone matters most: "I closed it and the same thing came back" is one of the two
          cases cloning was built for.
        */}
        <ul className="plain board-done-list">
          {finishedToday.map((task) => (
            <li key={task.id} data-testid="done-card" onContextMenu={(e) => onMenu(e, task)}>
              {/* Still live, so this strip is the undo for a drop you did not mean — and recorded,
                  so a reopen from here is named like any other move. */}
              <TaskStatusControl
                task={task}
                onAct={(status) => readback.record(status, [task])}
                onFail={readback.withdraw}
              />
              <button className="link" onClick={() => onOpen(task.id)}>
                {task.title}
              </button>
            </li>
          ))}
        </ul>
        {finishedToday.length === 0 && (
          <p className="muted">Drop a card here to finish it. Nothing yet today.</p>
        )}
      </section>
    </div>
  );
}
